<template>
  <div class="tw-mb-7">
    <WdsStack justify-space-between align-center>
      <WdsStack align-center>
        <ProfileInfoCard :global-id="globalKey">
          <WdsAvatar :path="getAvatarPath(avatarPath, globalKey)" class="tw-mr-3"></WdsAvatar>
        </ProfileInfoCard>
        <div>
          <a :href="profileUrl">{{ companyName }}</a>
          <div>
            <span v-if="!managesWorkers">
              <span>{{ recruiterFee ?? 0 }}%</span>
              <span class="tw-text-gray-600">&nbsp;{{ $t('fee') }}</span>
              <span class="separator tw-mx-1" />
            </span>
            <span>&nbsp;{{ ownershipDays ?? '?' }}</span>
            <span class="tw-text-gray-600">
              {{ ' ' + $tc(':count ownership days', ownershipDays) }}
            </span>
          </div>
        </div>
      </WdsStack>
      <div v-if="isInvited">
        <WdsStack gap-md>
          <a :href="acceptClientRoute">
            <WdsButton primary  :data-test="`accept-company-invitation-button-${globalKey}`">
              {{ $t('Accept') }}
            </WdsButton>
          </a>
          <WdsButton tertiary :data-test="`decline-company-invitation-button-${globalKey}`" @click="handleDeclineClient">
            {{ $t('Decline') }}
          </WdsButton>
        </WdsStack>
      </div>
      <div v-else>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <provide-state-triggered-by-slot-props v-slot="{ turnOff, toggle, isOn }">
          <BasePopper
            v-click-outside="turnOff"
            :display="isOn"
            :arrow="false"
            placement="left-start"
          >
            <i
              class="zmdi zmdi-more-horiz tw-text-gray-700 tw-cursor-pointer tw-text-lg"
              @click="toggle"
            />
            <template #popper>
              <div
                v-if="canLeaveClient"
                class="tw-p-sm tw-text-red-500 tw-cursor-pointer"
                @click="handleLeaveClient"
              >
                <WdsStack align-center class="menu-item tw-p-1.5 tw-max-w-[200px]">
                  <WdsIcon name="exit" class="tw-mr-2" style="display: inline" />
                  <span>{{ `${$t('Leave')} ${companyName}` }}</span>
                </WdsStack>
              </div>
              <div v-else class="tw-p-2.5 tw-max-w-[200px]">
                {{ $t('You cannot leave the client at this point as you have active relations.') }}
              </div>
            </template>
          </BasePopper>
        </provide-state-triggered-by-slot-props>
      </div>
    </WdsStack>
  </div>
  <WdsModal :show="showModal" :title="modalContent.title" @cancel="toggleShowModal(false)">
    {{ modalContent.body }}
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton @click="toggleShowModal(false)">
          {{ $t('Cancel') }}
        </WdsButton>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <BaseHtmlForm v-if="modalType === DECLINE" :action="declineClientRoute">
          <WdsButton error data-test="decline-company-invitation-button-on-modal">
            {{ $t('Decline') }}
          </WdsButton>
        </BaseHtmlForm>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <BaseHtmlForm v-else :action="leaveClientRoute" method="DELETE">
          <div>
            <input name="redirect_uri" type="hidden" :value="leaveClientRedirectRoute" />
            <WdsButton error>
              {{ $t('Leave') }}
            </WdsButton>
          </div>
        </BaseHtmlForm>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup>
import { WdsStack, WdsButton, WdsAvatar, WdsModal, WdsIcon } from '@worksome/wds-components'
import { defineProps, ref, computed } from 'vue'
import ProfileInfoCard from '@/components/ProfileInfoCard.vue'
import BasePopper from '@/components/BasePopper.vue'
import { getAvatarPath } from '@/utils/avatar'

const props = defineProps({
  globalKey: {
    type: String,
    default: undefined,
  },
  avatarPath: {
    type: String,
    default: undefined,
  },
  profileUrl: {
    type: String,
    default: undefined,
  },
  companyName: {
    type: String,
    default: undefined,
  },
  recruiterFee: {
    type: Number,
    default: undefined,
  },
  managesWorkers: {
    type: Boolean,
    default: false,
  },
  ownershipDays: {
    type: Number,
    default: undefined,
  },
  isInvited: {
    type: Boolean,
    default: false,
  },
  acceptClientRoute: {
    type: String,
    default: undefined,
  },
  declineClientRoute: {
    type: String,
    default: undefined,
  },
  leaveClientRoute: {
    type: String,
    default: undefined,
  },
  canLeaveClient: {
    type: Boolean,
    default: false,
  },
  leaveClientRedirectRoute: {
    type: String,
    default: undefined,
  },
})

const DECLINE = 'DECLINE'
const LEAVE = 'LEAVE'

// Leave client functionality
const leaveClientTitle = `${$t('Leaving')} ${props.companyName}?`
const leaveClientBody = $t(
  'Are you sure you want to leave :client? By leaving we will remove all the relations you have with :client.',
  { client: props.companyName },
)

// Decline client functionality
const declineClientTitle = `${$t('Decline a request from')} ${props.companyName}`
const declineClientBody = $t(
  'Are you sure you want to decline a request from :client? Once you decline ' +
    "we will notify :client that you've declined the request.",
  { client: props.companyName },
)

const showModal = ref(false)
const modalType = ref(DECLINE)
const modalContent = computed(() => {
  if (modalType.value === DECLINE) {
    return {
      title: declineClientTitle,
      body: declineClientBody,
    }
  }

  return {
    title: leaveClientTitle,
    body: leaveClientBody,
  }
})

const handleLeaveClient = () => {
  showModal.value = true
  modalType.value = LEAVE
}

const handleDeclineClient = () => {
  showModal.value = true
  modalType.value = DECLINE
}

const toggleShowModal = (show) => {
  showModal.value = show
}
</script>

<style scoped lang="scss">
.separator:before {
  content: '\B7';
}

.menu-item {
  &:hover {
    @apply tw-bg-red-50;
  }
}
</style>
